import { v4 as uuidv4 } from "uuid";
import { useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCollapsePanel } from "hooks/useCollapsePanel";
import { getNameWithPercentageSplit } from "js/formatting";
import { isFlagEnabled } from "js/utilities";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";
import ContactUsDialog from "components/ModalDialogs/ContactUsDialog";
import { ChatContext } from "context/Agent/ChatProvider";

const BeneficiariesCard = ({ policyDetail }) => {
  const uuid = uuidv4();
  const { openChatWindow, setOpenChatWindow } = useContext(ChatContext);
  const { t } = useTranslation();
  const [contactUsDialogState, setContactUsDialogState] = useState(false);

  useCollapsePanel(uuid);
  const isPolicyHasBeneficiary = () => {
    return (
      policyDetail?.beneficiaries?.primary?.length > 0 ||
      policyDetail?.beneficiaries?.contingent.length > 0
    );
  };

  return (
    <>
      <div className="ui-card">
        <div className="p-4 xl:pt-6 xl:px-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <h2 className="mb-0">
              <button
                id={uuid}
                className="flex items-center group md:hidden"
                aria-controls={`${uuid}-panel`}
                aria-expanded="true"
                aria-label={t(
                  "AgentBeneficiariesCard-expand-collapse-btn-label"
                )}
              >
                <span className="inline-block p-1 rounded-full mr-1 text-ui-blue group-hover:text-white group-hover:bg-ui-blue">
                  <svg
                    className="ui-icon ui-plus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-plus"></use>
                  </svg>
                  <svg
                    className="ui-icon ui-minus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-minus"></use>
                  </svg>
                </span>
                <span
                  className={`text-base font-body font-bold md:ui-no-badge-icon-inline`}
                >
                  {t("AgentBeneficiariesCard-title")}
                </span>
              </button>
              <span className="text-xl font-body font-bold hidden md:block">
                {t("AgentBeneficiariesCard-title")}
              </span>
            </h2>
          </div>
        </div>
        <hr className="ui-collapse" />
        {isPolicyHasBeneficiary() && (
          <>
            <div
              id={`${uuid}-panel`}
              className="overflow-hidden h-20 md:h-auto flex flex-col md:flex-row"
            >
              {policyDetail?.beneficiaries?.primary?.length > 0 && (
                <>
                  <div className="flex-1 p-4 pb-6 xl:p-6 xl:pt-0 pt-0">
                    <div className="text-xs text-theme-text-tertiary pt-4 xl:pt-6">
                      {t("AgentBeneficiariesCard-label-1")}
                    </div>
                    <div className="grid grid-cols-1">
                      {policyDetail?.beneficiaries?.primary?.map((p, idx) => {
                        return (
                          <Trans
                            i18nKey={getNameWithPercentageSplit(
                              p?.person,
                              p?.splitPercent,
                              p?.sharedEquallyFlag,
                              policyDetail?.beneficiaries?.primary?.length
                            )}
                            components={{
                              div1: (
                                <div className="text-sm text-theme-text-secondary font-semibold whitespace-nowrap" />
                              ),
                              div2: (
                                <span className="text-theme-text-tertiary" />
                              ),
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              {policyDetail?.beneficiaries?.contingent?.length > 0 && (
                <>
                  <div className="flex-1 p-4 xl:p-6 pb-6 xl:pt-0 pt-0">
                    <div className="text-xs text-theme-text-tertiary pt-4 xl:pt-6">
                      {t("AgentBeneficiariesCard-label-2")}
                    </div>
                    <div className="grid grid-cols-1">
                      {policyDetail?.beneficiaries?.contingent?.map(
                        (item, idx) => {
                          return (
                            <Trans
                              i18nKey={getNameWithPercentageSplit(
                                item?.person,
                                item?.splitPercent,
                                item?.sharedEquallyFlag,
                                policyDetail?.beneficiaries?.contingent?.length
                              )}
                              components={{
                                div1: (
                                  <div className="text-sm text-theme-text-secondary font-semibold whitespace-nowrap" />
                                ),
                                div2: (
                                  <span className="text-theme-text-tertiary" />
                                ),
                              }}
                            />
                          );
                        }
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
        <CardDisclaimerBase
          disclaimer={
            <Trans
              i18nKey={
                isPolicyHasBeneficiary()
                  ? "AgentBeneficiariesCard-disclaimer-withBeneficiary"
                  : "AgentBeneficiariesCard-disclaimer-noBeneficiary"
              }
              components={{
                button1: (
                  <button
                    onClick={() => setOpenChatWindow(!openChatWindow)}
                    className="ui-btn-hyperlink text-xs font-semibold"
                  ></button>
                ),
                button2: (
                  <button
                    onClick={() => setContactUsDialogState(true)}
                    className="ui-btn-hyperlink text-xs font-semibold ml-0"
                  ></button>
                ),
              }}
            ></Trans>
          }
        />
        <ContactUsDialog
          dialogState={contactUsDialogState}
          setDialogState={setContactUsDialogState}
        />
      </div>
    </>
  );
};

export default BeneficiariesCard;
